export interface OriginBlock {
  tag: string;
  en: string;
  title: string;
  text: string;
}

export interface StoryChapter {
  heading: string;
  paragraphs: string[];
}

export interface ProfileItem {
  label: string;
  value: string;
}

export interface GalleryItem {
  image: string;
  caption: string;
}

export interface CharacterDetail {
  id: string;
  intro: string;
  originBlocks: OriginBlock[];
  story: StoryChapter[];
  storySubtitle: string;
  profile: ProfileItem[];
  quote: string;
  gallery: GalleryItem[];
}

export const characterDetails: CharacterDetail[] = [
  {
    id: 'yu-doudou',
    intro:
      '鱼豆豆是 NOMOISM 的第一个 NOMO 灵。一味是鱼，一境是西湖，一愿是好生活。它圆滚滚、软乎乎，从西湖游出来，把好运吐给每一个遇见它的人。',
    originBlocks: [
      {
        tag: '一味',
        en: 'FLAVOR',
        title: '西湖醋鱼',
        text:
          '杭州菜里的名角，酸甜交织，像人生有酸也有甜。一道西湖醋鱼，藏着江南人对「年年有余」最朴素的盼望。',
      },
      {
        tag: '一境',
        en: 'PLACE',
        title: '杭州 · 西湖',
        text:
          '三面云山一面城，西湖是江南最温柔的一片水。苏堤春晓、曲院风荷，温柔是这座城写在骨子里的底色。',
      },
      {
        tag: '一愿',
        en: 'WISH',
        title: '好运 · 好生活',
        text:
          '鱼跃龙门，讨一个「年年有余」的好彩头。鱼豆豆想替每个疲惫的人，把「好运」和「松弛」重新找回来。',
      },
    ],
    story: [
      {
        heading: '一条没有名字的小鱼',
        paragraphs: [
          '很久以前，西湖里住着一条圆滚滚的小鱼。它没有名字，每天在荷叶底下游来游去，听南来北往的人站在苏堤上许愿。',
          '它见过清晨划船的船娘，见过黄昏散步的老人，也见过深夜还舍不得离开的年轻人。西湖的水，装得下所有人的心事。',
        ],
      },
      {
        heading: '苏堤上的愿望',
        paragraphs: [
          '有人盼考试高中，有人盼家人平安，有人盼早点回家。小鱼听着听着，就把这些愿望都记在了心里，一颗一颗，像珍珠一样，沉在湖底闪闪发亮。',
          '它想，这些愿望多美好啊，可惜看不见、也摸不着。要是能让它们变得触手可及，该有多好。',
        ],
      },
      {
        heading: '一个会发光的泡泡',
        paragraphs: [
          '有一天，一个姑娘在湖边轻声说：「要是能把这些愿望都变成看得见、摸得着的东西，该多好。」小鱼鼓了鼓腮帮，吐出一个泡泡，泡泡里映着西湖的月色，还有姑娘的笑脸。',
          '那一瞬间，它懂了。它想要的，不是替别人实现愿望，而是把愿望本身，变成一份可以被带走的温柔。',
        ],
      },
      {
        heading: '于是，鱼豆豆游上了岸',
        paragraphs: [
          '小鱼游上岸，变成了「鱼豆豆」—— 圆滚滚、软乎乎，带着杭州的温柔，也带着一点松弛自在。它走到哪，就把好运吐到哪。',
          '从此，每个遇见鱼豆豆的人，都会在口袋里摸到一颗温热的、小小的幸运。',
        ],
      },
    ],
    storySubtitle: '苏堤上的愿望',
    profile: [
      { label: '性格', value: '温柔 · 松弛 · 爱笑' },
      { label: '喜好', value: '荷花 · 桂花糖 · 游湖' },
      { label: '技能', value: '吐一个泡泡，许一个愿' },
      { label: '心愿', value: '把好运带给每一个疲惫的人' },
    ],
    quote: '愿你，也被生活温柔以待，好运常伴。',
    gallery: [
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20rounded%20plump%20little%20fish%20designer%20art%20toy%20figure%20with%20soft%20cream%20and%20warm%20orange%20gradient%20body%20sitting%20on%20a%20green%20lotus%20leaf%20floating%20on%20water%20small%20pink%20lotus%20flower%20on%20its%20head%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yudoudou-01&orientation=squarish',
        caption: '荷叶上的鱼豆豆',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20fish%20designer%20art%20toy%20figure%20with%20soft%20cream%20and%20warm%20orange%20body%20blowing%20transparent%20soap%20bubbles%20filled%20with%20tiny%20golden%20stars%20floating%20around%20it%20gentle%20smiling%20face%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yudoudou-02&orientation=squarish',
        caption: '许一个愿望',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20fish%20designer%20art%20toy%20figure%20hugging%20a%20small%20amber%20osmanthus%20candy%20jar%20with%20tiny%20yellow%20osmanthus%20flowers%20around%20cream%20and%20warm%20orange%20body%20cozy%20smiling%20face%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yudoudou-03&orientation=squarish',
        caption: '抱着一罐桂花糖',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20fish%20designer%20art%20toy%20figure%20three%20different%20expressions%20happy%20sleepy%20thinking%20arranged%20in%20a%20row%20cream%20and%20warm%20orange%20gradient%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yudoudou-04&orientation=squarish',
        caption: '三种小表情',
      },
    ],
  },
  {
    id: 'la-dundun',
    intro:
      '辣墩墩是一锅热辣里长大的 NOMO 灵。一味是麻辣火锅，一境是山城江湖，一愿是日子红火。它脾气热、心肠软，谁靠近都想被它请一顿。',
    originBlocks: [
      {
        tag: '一味',
        en: 'FLAVOR',
        title: '麻辣火锅',
        text:
          '九宫格里翻滚的红汤，麻辣鲜香，是重庆最滚烫的江湖气。一筷子涮下去，热闹和痛快就一起起来了。',
      },
      {
        tag: '一境',
        en: 'PLACE',
        title: '重庆 · 山城',
        text:
          '两江交汇，吊脚楼依山而建，轻轨穿楼而过。重庆是一座立体的城，热辣是它写进雾都里的性格底色。',
      },
      {
        tag: '一愿',
        en: 'WISH',
        title: '红火 · 热闹',
        text:
          '辣墩墩想把日子过成一锅沸腾的火锅，热气腾腾、红红火火。谁来了，都有一双筷子、一个热乎的座位。',
      },
    ],
    story: [
      {
        heading: '山城里的一锅热汤',
        paragraphs: [
          '重庆的雾，常常从清晨就厚厚地铺开。山城的台阶上，有一口咕嘟咕嘟冒着泡的老火锅，它不知道自己从什么时候开始，就一直这么热着。',
          '锅底的红汤翻滚着，辣椒和花椒在水里跳舞。路过的挑夫、下班的工人、放学的小孩，都会在这口锅前停下来，暖暖手。',
        ],
      },
      {
        heading: '九宫格里的江湖',
        paragraphs: [
          '有人来，是为了吃一口最鲜的毛肚；有人来，是为了和几个老友碰杯。九宫格一格一格，装着不同的讲究，也装着一座城的江湖气。',
          '火锅看着热闹，心里却明白：再滚烫的汤，也得有人愿意围坐，才叫团圆。',
        ],
      },
      {
        heading: '一团不肯熄灭的火',
        paragraphs: [
          '有一次，一个年轻人坐在锅边叹气，说日子过得没劲。火锅听了，把火苗烧得更旺了些，红汤翻得更欢，像是在说：日子嘛，要滚起来才带劲。',
          '它想，人要是能像这一锅汤一样，永远热气腾腾、不肯熄火，该有多好。',
        ],
      },
      {
        heading: '于是，辣墩墩上了桌',
        paragraphs: [
          '这口火锅慢慢有了形状，变成了「辣墩墩」—— 圆滚滚、红彤彤，脾气热、心肠软。它走到哪，就把哪里的桌子支起来。',
          '从此，每个遇见辣墩墩的人，心里都像被点了一把火，热乎乎的，想把日子过得红红火火。',
        ],
      },
    ],
    storySubtitle: '九宫格里的江湖',
    profile: [
      { label: '性格', value: '热辣 · 爽快 · 讲义气' },
      { label: '喜好', value: '毛肚 · 山城啤酒 · 摆龙门阵' },
      { label: '技能', value: '把冷场烧成一锅热汤' },
      { label: '心愿', value: '让每一桌都坐满热闹的人' },
    ],
    quote: '日子嘛，要滚起来才带劲，越滚越红火。',
    gallery: [
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20chubby%20hotpot%20themed%20designer%20art%20toy%20character%20with%20rounded%20deep%20red%20and%20warm%20orange%20gradient%20body%20wearing%20a%20tiny%20chili%20pepper%20crown%20sitting%20beside%20a%20small%20red%20hotpot%20bowl%20with%20rising%20steam%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-ladundun-01&orientation=squarish',
        caption: '火锅旁的一团火',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20chubby%20hotpot%20themed%20designer%20art%20toy%20character%20in%20deep%20red%20and%20warm%20orange%20colors%20blowing%20a%20burst%20of%20spicy%20red%20flame%20from%20its%20smiling%20mouth%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-ladundun-02&orientation=squarish',
        caption: '喷一口热辣',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20chubby%20hotpot%20themed%20designer%20art%20toy%20character%20hugging%20a%20big%20plate%20of%20fresh%20tripe%20slices%20and%20red%20chili%20peppers%20deep%20red%20and%20warm%20orange%20body%20cozy%20grin%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-ladundun-03&orientation=squarish',
        caption: '抱着一盘毛肚',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20chubby%20hotpot%20themed%20designer%20art%20toy%20character%20with%20three%20different%20expressions%20happy%20spicy%20and%20sleepy%20arranged%20in%20a%20row%20deep%20red%20and%20warm%20orange%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-ladundun-04&orientation=squarish',
        caption: '三种小表情',
      },
    ],
  },
  {
    id: 'mian-changchang',
    intro:
      '面长长是一条慢吞吞的 NOMO 灵。一味是甜水面，一境是天府闲适，一愿是长长久久。它把每一份祝福，都拉成一条又长又韧的面。',
    originBlocks: [
      {
        tag: '一味',
        en: 'FLAVOR',
        title: '甜水面',
        text:
          '成都街头的一碗甜水面，甜中带辣、面条粗壮有嚼劲。一筷子下去，是川西坝子最朴实的满足。',
      },
      {
        tag: '一境',
        en: 'PLACE',
        title: '成都 · 天府',
        text:
          '平原沃野，茶馆里的龙门阵一摆就是一下午。成都不慌不忙，把「安逸」过成了一种生活哲学。',
      },
      {
        tag: '一愿',
        en: 'WISH',
        title: '长久 · 康健',
        text:
          '面条越长，情意越长。面长长想把「长长久久」的祝福，绕进每一个平凡而安稳的日子里。',
      },
    ],
    story: [
      {
        heading: '一碗慢吞吞的面',
        paragraphs: [
          '成都的下午，阳光斜斜地洒在巷子里。有一碗甜水面，静静地待在竹桌上，酱汁慢慢渗进面条里，谁也不催它。',
          '这座城市的人，走路都不急。面也一样，慢吞吞地把甜和辣搅匀，把每一根面条都浸得透透的。',
        ],
      },
      {
        heading: '甜辣里的心意',
        paragraphs: [
          '甜水面看着简单，其实讲究得很：酱要熬得浓，面要拉得粗，甜和辣要刚刚好。就像过日子，酸甜苦辣都得拿捏住。',
          '它想，人这一辈子，能稳稳当当、不慌不忙地把日子过下去，就是顶好的福气。',
        ],
      },
      {
        heading: '一根拉不断的长面',
        paragraphs: [
          '有个阿婆坐在面摊前，说盼着家里人平平安安、长长久久。面听了，把自己拉得更长了些，一根到底，怎么也拉不断。',
          '它想，祝福要是能像这根面一样，又长又韧、延绵不断，该有多好。',
        ],
      },
      {
        heading: '于是，面长长绕上了桌',
        paragraphs: [
          '这碗面慢慢有了形状，变成了「面长长」—— 慢吞吞、温吞吞，把每一份祝福都绕成一条又长又韧的面。',
          '从此，每个遇见面长长的人，心里都多了一份笃定：慢一点没关系，心意拉得长，才够用一辈子。',
        ],
      },
    ],
    storySubtitle: '一碗面的长长久久',
    profile: [
      { label: '性格', value: '温吞 · 耐心 · 念旧' },
      { label: '喜好', value: '甜水面 · 盖碗茶 · 晒太阳' },
      { label: '技能', value: '把祝福拉成一根长面' },
      { label: '心愿', value: '愿身边的人长长久久、平平安安' },
    ],
    quote: '慢一点没关系，心意拉得长，才够用一辈子。',
    gallery: [
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20little%20noodle%20themed%20designer%20art%20toy%20character%20in%20cream%20and%20wheat%20golden%20tones%20with%20a%20long%20flowing%20noodle%20scarf%20wrapped%20around%20its%20body%20gentle%20smile%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-mianchangchang-01&orientation=squarish',
        caption: '绕一条长长的面',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20little%20noodle%20themed%20designer%20art%20toy%20character%20holding%20a%20warm%20bowl%20of%20sweet%20noodles%20with%20red%20chili%20oil%20cream%20and%20wheat%20golden%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-mianchangchang-02&orientation=squarish',
        caption: '端一碗甜水面',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20little%20noodle%20themed%20designer%20art%20toy%20character%20twirling%20a%20very%20long%20single%20noodle%20around%20its%20small%20hand%20cream%20and%20wheat%20golden%20tones%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-mianchangchang-03&orientation=squarish',
        caption: '把祝福拉长',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20little%20noodle%20themed%20designer%20art%20toy%20character%20with%20three%20different%20expressions%20happy%20sleepy%20and%20thinking%20arranged%20in%20a%20row%20cream%20and%20wheat%20golden%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-mianchangchang-04&orientation=squarish',
        caption: '三种小表情',
      },
    ],
  },
  {
    id: 'ya-longlong',
    intro:
      '鸭珑珑是一只讲究的 NOMO 灵。一味是北京烤鸭，一境是皇城胡同，一愿是圆满富足。它外表油亮讲究，内里却是热心肠的老北京。',
    originBlocks: [
      {
        tag: '一味',
        en: 'FLAVOR',
        title: '北京烤鸭',
        text:
          '果木炭火烤出的枣红鸭皮，酥香流油，配上一张薄饼、几缕甜面酱，是京城最体面的待客之道。',
      },
      {
        tag: '一境',
        en: 'PLACE',
        title: '北京 · 皇城',
        text:
          '红墙黄瓦与胡同灰砖，古老与鲜活并存。北京城大气、讲究，也藏着一份热气腾腾的人情。',
      },
      {
        tag: '一愿',
        en: 'WISH',
        title: '圆满 · 富足',
        text:
          '鸭珑珑想替每个人讨一个「圆满」，日子过得有滋有味，生活富足又体面。',
      },
    ],
    story: [
      {
        heading: '胡同里最讲究的一只鸭',
        paragraphs: [
          '北京的胡同里，住着一只特别讲究的鸭。它每天都要把羽毛捋得油亮，走路也得挺着胸脯，一副京城大宅门的气派。',
          '可别被它的外表唬住。邻里街坊谁家有个难处，头一个张罗帮忙的，准是它。',
        ],
      },
      {
        heading: '枣红皮下的热心肠',
        paragraphs: [
          '它最爱的，是傍晚胡同口那家烤鸭店飘出来的香。枣红色的鸭皮在炭火上滋滋作响，整条巷子都跟着热闹起来。',
          '它想，一顿讲究的饭，从来不只是吃个味道，更是一家人坐在一起的那份热乎劲儿。',
        ],
      },
      {
        heading: '一张薄饼的圆满',
        paragraphs: [
          '有一次，一位老人坐在胡同口，说孩子们都忙，好久没能凑齐吃顿饭。鸭听了，默默把一张薄饼卷得漂漂亮亮，递到老人手里。',
          '它想，日子要是有滋有味、团团圆圆，就是这世上最体面的事。',
        ],
      },
      {
        heading: '于是，鸭珑珑亮了相',
        paragraphs: [
          '这只讲究的鸭，慢慢变成了「鸭珑珑」—— 外表油亮、内里热心，走到哪都带着一股子老北京的体面劲儿。',
          '从此，每个遇见鸭珑珑的人，都会记得：再小的一顿饭，也要吃得体面，过得圆满。',
        ],
      },
    ],
    storySubtitle: '胡同里的讲究',
    profile: [
      { label: '性格', value: '讲究 · 体面 · 热心' },
      { label: '喜好', value: '胡同 · 京剧 · 京味小吃' },
      { label: '技能', value: '把日子片得薄而圆满' },
      { label: '心愿', value: '愿每一桌都圆圆满满、富足体面' },
    ],
    quote: '再小的一顿饭，也要吃得体面，过得圆满。',
    gallery: [
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20round%20roast%20duck%20themed%20designer%20art%20toy%20character%20with%20glossy%20golden%20brown%20caramel%20body%20wearing%20a%20tiny%20red%20imperial%20collar%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yalonglong-01&orientation=squarish',
        caption: '戴着小朝珠',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20round%20roast%20duck%20themed%20designer%20art%20toy%20character%20wrapping%20itself%20in%20a%20thin%20pancake%20with%20spring%20onion%20and%20sweet%20bean%20sauce%20golden%20brown%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yalonglong-02&orientation=squarish',
        caption: '裹一张薄饼',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20round%20roast%20duck%20themed%20designer%20art%20toy%20character%20standing%20proudly%20on%20a%20small%20red%20pedestal%20like%20a%20palace%20treasure%20golden%20brown%20caramel%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yalonglong-03&orientation=squarish',
        caption: '皇城里的讲究',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20round%20roast%20duck%20themed%20designer%20art%20toy%20character%20with%20three%20different%20expressions%20proud%20happy%20and%20sleepy%20arranged%20in%20a%20row%20golden%20brown%20caramel%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-yalonglong-04&orientation=squarish',
        caption: '三种小表情',
      },
    ],
  },
  {
    id: 'xia-jiaojiao',
    intro:
      '虾饺饺是一笼点心里的 NOMO 灵。一味是早茶虾饺，一境是岭南水乡，一愿是团圆聚首。它最爱热闹，一笼就能召唤一大家子人围坐。',
    originBlocks: [
      {
        tag: '一味',
        en: 'FLAVOR',
        title: '早茶虾饺',
        text:
          '半透明的澄面皮裹着鲜虾，晶莹剔透、鲜甜弹牙。一笼虾饺，是岭南早茶桌上最受欢迎的主角。',
      },
      {
        tag: '一境',
        en: 'PLACE',
        title: '广东 · 岭南',
        text:
          '水乡河网密布，骑楼下茶楼飘香。岭南人爱热闹，一盅两件，就能把清晨过成一场欢聚。',
      },
      {
        tag: '一愿',
        en: 'WISH',
        title: '团圆 · 聚首',
        text:
          '虾饺饺想把散落各地的家人朋友，都聚到一张桌前，热气腾腾地吃上一顿团圆饭。',
      },
    ],
    story: [
      {
        heading: '蒸笼里的一颗小虾饺',
        paragraphs: [
          '岭南的清晨，茶楼里蒸汽袅袅。一笼虾饺刚刚出笼，晶莹剔透的皮子裹着鲜甜的虾仁，静静地冒着热气。',
          '它是这一桌点心里最俏皮的那一个，白里透粉，谁见了都想先夹它一口。',
        ],
      },
      {
        heading: '一盅两件的早晨',
        paragraphs: [
          '阿公阿婆、阿爸阿妈、还有几个吵吵闹闹的小朋友，围坐一桌。一盅两件，虾饺、烧卖、凤爪，摆得满满当当。',
          '虾饺最爱这样的早晨。它想，人这一辈子最难得的，不是吃得多好，而是愿意早起、坐在一起吃顿早茶的人。',
        ],
      },
      {
        heading: '一张不肯散的桌',
        paragraphs: [
          '有一年，家里的小辈去了远方读书，早茶桌上少了一副碗筷。虾饺看在眼里，悄悄把自己蒸得更加饱满，像是在说：等我，等大家回来。',
          '它想，团圆从来不是一蹴而就，而是一点一点、把散开的人重新聚回来的耐心。',
        ],
      },
      {
        heading: '于是，虾饺饺出了笼',
        paragraphs: [
          '这颗小虾饺慢慢有了形状，变成了「虾饺饺」—— 白里透粉、俏皮爱笑，一笼就能召唤一大家子人。',
          '从此，每个遇见虾饺饺的人，都会想起那张热热闹闹、不肯散场的早茶桌。',
        ],
      },
    ],
    storySubtitle: '一笼点心的团圆',
    profile: [
      { label: '性格', value: '活泼 · 爱热闹 · 嘴甜' },
      { label: '喜好', value: '早茶 · 煲汤 · 一家人围坐' },
      { label: '技能', value: '一笼召唤一桌人' },
      { label: '心愿', value: '愿每个团圆的日子都不散场' },
    ],
    quote: '一家人围坐一桌，就是这世上最暖的早茶。',
    gallery: [
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20shrimp%20dumpling%20themed%20designer%20art%20toy%20character%20with%20translucent%20pink%20and%20ivory%20white%20body%20sitting%20inside%20a%20small%20round%20bamboo%20steamer%20basket%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-xiajiaojiao-01&orientation=squarish',
        caption: '蒸笼里的虾饺饺',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20shrimp%20dumpling%20themed%20designer%20art%20toy%20character%20holding%20a%20small%20porcelain%20teacup%20of%20jasmine%20tea%20translucent%20pink%20and%20ivory%20white%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-xiajiaojiao-02&orientation=squarish',
        caption: '喝一口茉莉茶',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20shrimp%20dumpling%20themed%20designer%20art%20toy%20character%20surrounded%20by%20a%20happy%20family%20of%20smaller%20dumplings%20sitting%20together%20at%20a%20round%20table%20translucent%20pink%20and%20ivory%20white%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-xiajiaojiao-03&orientation=squarish',
        caption: '一家子围坐',
      },
      {
        image:
          'https://readdy.ai/api/search-image?query=Cute%20plump%20shrimp%20dumpling%20themed%20designer%20art%20toy%20character%20with%20three%20different%20expressions%20happy%20shy%20and%20sleepy%20arranged%20in%20a%20row%20translucent%20pink%20and%20ivory%20white%20body%20plain%20warm%20beige%20studio%20background%20soft%20shadows%20collectible%20vinyl%20toy%20product%20photography%20high%20detail&width=1000&height=1000&seq=nomo-gallery-xiajiaojiao-04&orientation=squarish',
        caption: '三种小表情',
      },
    ],
  },
];